/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React from 'react';
import Select, { components } from 'react-select';
import { Props as SelectStylesProp } from 'react-select/src/styles';
import { ActionMeta, Theme, ValueType } from 'react-select/src/types';

import styled from '../../styled';
import ArrowDropDown from '../icons/ArrowDropDown';
import CheckboxChecked from '../icons/CheckboxChecked';
import CheckboxUnchecked from '../icons/CheckboxUnchecked';
import MetaLabel from '../text/MetaLabel';
import { creatUuId, reactSelectStyles } from '../utils';

import CopyButton from './CopyButton';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

interface WrapProps extends React.HTMLAttributes<HTMLDivElement> {
	type?: string;
	hasFocus?: boolean;
	error?: string;
	theme?: any;
}

export interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	original?: any;
	error?: string;
	canCopy?: boolean;
	icon?: React.ReactNode;
	wrapProps?: React.HTMLAttributes<HTMLDivElement>;
	showChanged?: boolean;
	isBig?: boolean;
	options?: Array<{ value: string; label: string }>;
	selectStyles?: SelectStylesProp;
	onDropdownChange?: (value: ValueType<any>, meta: ActionMeta) => void;
	custom?: React.ReactNode;
}

const wrapStyles = ({ type, hasFocus, theme }: WrapProps) => {
	const result = ['position: relative;'];
	if (type === 'checkbox') {
		result.push(`
			cursor: pointer;
			display: flex;
			flex-flow: row-reverse nowrap;
			justify-content: center;
			align-items: center;`);
		if (hasFocus) {
			result.push(`
				fill: ${theme.color.primary};
			`);
		}
	} else if (type === 'select') {
		result.push(`
			display: flex;
			flex-direction: column;
			align-items: stretch;
			justify-content: center;
		`);
	} else {
		result.push('margin: 0.4em 0;');
	}
	return result;
};

const Wrap = styled('div')<WrapProps>`
	${(props) => wrapStyles(props)};
`;

const Input = styled('input')<InputProps>`
	appearance: none;
	transition: all 0.1s linear;
	font: inherit;
	letter-spacing: inherit;
	display: block;
	padding: ${(props) => props.theme.form.input.padding};
	box-sizing: border-box;
	width: 100%;
	margin: 0;

	font-weight: ${(props) => props.theme.font.weight.thin};
	font-size: ${(props) => props.theme.form.input.fontSize};

	border-radius: ${(props) => props.theme.form.input.borderRadius};
	border: 1px solid transparent;

	background-color: ${(props) =>
		props.disabled ? props.theme.form.input.backgroundColorDisabled : props.theme.form.input.backgroundColor};
	color: ${(props) => (props.disabled ? props.theme.form.input.colorDisabled : props.theme.form.input.color)};

	&:focus {
		box-shadow: 0 0 1em ${(props) => props.theme.color.primaryLight};
		color: ${(props) => props.theme.form.input.colorFocused};
		background-color: ${(props) => props.theme.form.input.backgroundColorFocused};
		border-color: ${(props) => props.theme.color.primary};
		outline: 0;
	}
`;

const Icon = styled('div')`
	width: 2.6em;
	height: 2.6em;
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	top: 50%;
	margin-top: -1.3em;
	left: 0;
	pointer-events: none;
`;

const Children = styled('div')`
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	display: flex;
	justify-content: flex-end;
`;

const Checkbox = styled('input')<InputProps>`
	appearance: none;
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	cursor: pointer;

	&:focus {
		outline: none;
	}
`;

const DirtyWrap = styled('div')`
	font-weight: ${(props) => props.theme.font.weight.regular};
	color: ${(props) => props.theme.color.warning};
`;

const ErrorWrap = styled('div')`
	font-weight: ${(props) => props.theme.font.weight.regular};
	color: ${(props) => props.theme.color.error};
`;

const labelClass = css`
	display: block;
	padding: 0.4em 0;
`;

const checkboxLabelClass = css`
	display: block;
	padding-left: 1ex;
	text-transform: none;
`;

const SelectIndicator = components.DropdownIndicator!;

const DropdownIndicator = (props: any) => {
	return (
		<SelectIndicator {...props}>
			<ArrowDropDown />
		</SelectIndicator>
	);
};

const extendTheme = (theme: Theme) => ({
	...theme,
	borderRadius: 0
});

export const InputField: React.FC<InputFieldProps> = ({
	children,
	label,
	original,
	error,
	wrapProps,
	name,
	canCopy,
	icon,
	showChanged,
	isBig,
	options,
	onDropdownChange,
	selectStyles,
	...props
}) => {
	const [focus, setFocus] = React.useState(false);
	const [uuid] = React.useState('field-' + creatUuId());

	const onCheckboxFocus = () => setFocus(true);
	const onCheckboxBlur = () => setFocus(false);

	const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		const el = e.target;
		const placeholder = el.getAttribute('placeholder');
		if (placeholder) {
			el.setAttribute('data-placeholder', el.getAttribute('placeholder') || '');
			el.setAttribute('placeholder', '');
		}
		setFocus(true);
		if (props.onFocus) {
			e.persist();
			props.onFocus(e);
		}
	};
	const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const el = e.target;
		const placeholder = el.getAttribute('data-placeholder');
		if (placeholder) {
			el.setAttribute('placeholder', el.getAttribute('data-placeholder') || '');
		}
		setFocus(false);
	};

	const dirty = showChanged && original !== props.value;
	const fieldProps: InputProps = {
		...props,
		name: name ? name : uuid,
		id: uuid
	};
	const type = props.type || 'text';

	const selStyles = { ...reactSelectStyles };
	if (selectStyles) {
		// Merge select styles functions
		Object.keys(selectStyles).forEach((k) => {
			if (reactSelectStyles[k]) {
				selStyles[k] = (base: any, state: any = null) => selectStyles[k](reactSelectStyles[k](base, state));
			} else {
				selStyles[k] = (base: any, state: any = null) => selectStyles[k](base, state);
			}
		});
	}

	return (
		<Wrap hasFocus={focus} type={type} {...wrapProps}>
			{label && (
				<MetaLabel
					css={type === 'checkbox' ? checkboxLabelClass : labelClass}
					htmlFor={uuid}
					label={label}
					aside={
						error ? (
							<ErrorWrap>{error}</ErrorWrap>
						) : dirty ? (
							<DirtyWrap>Changed</DirtyWrap>
						) : canCopy ? (
							<CopyButton value={(fieldProps.value && fieldProps.value.toString()) || ''} />
						) : null
					}
				/>
			)}
			{type === 'checkbox' ? (
				<React.Fragment>
					<Checkbox onFocus={onCheckboxFocus} onBlur={onCheckboxBlur} {...fieldProps} />
					{props.checked ? <CheckboxChecked /> : <CheckboxUnchecked />}
				</React.Fragment>
			) : type === 'select' ? (
				<Select
					name={fieldProps.name}
					style={fieldProps.style}
					isClearable={false}
					theme={extendTheme}
					components={{ DropdownIndicator }}
					styles={selStyles}
					value={options ? options.find((o) => o.value === fieldProps.value + '') : null}
					onChange={onDropdownChange}
					isDisabled={fieldProps.disabled}
					options={options}
					isSearchable={false}
					placeholder={fieldProps.placeholder}
					// Now this is not really what's happening here. But we'll keep it this way to make to satisfy TypeScript while not overdoing it with the types.
					// Maybe separate Input components instead of one huge do it all one would have been a better idea?
					defaultValue={fieldProps.defaultValue as string[]}
				/>
			) : type === 'custom' && props.custom ? (
				props.custom
			) : (
				<Input {...fieldProps} onFocus={onInputFocus} onBlur={onInputBlur} />
			)}
			{icon && <Icon>{icon}</Icon>}
			<Children>{children}</Children>
		</Wrap>
	);
};

export default InputField;
